import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Spin,
  message,
  Divider,
  Button,
  Typography,
  Select,
  Input,
  Tag,
  Radio,
  Empty,
  Pagination,
} from "antd";
import React, { useCallback, useMemo, useState } from "react";
import {
  FilterOutlined,
  FileExcelOutlined,
  SearchOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import XLSX from "xlsx-js-style";
import { HEADERS_EXPORT_BEN, POST_LOAD_JUDGE_ASSET } from "../../API/apiUrls";
import CurrencyFormat from "../../../hook/CurrencyFormat";
import MotionHoc from "../../../utils/MotionHoc";
import TokenCheck from "../../../hook/TokenCheck";

const { Text } = Typography;

const OPTIONS_CASE_TYPE = [
  { value: 1, label: "ทำยอม" },
  { value: 2, label: "พิพากษาปกติ" },
  { value: 3, label: "พิพากษาปกติและแบบตารางดิว" },
];

const OPTIONS_FILTER_CASE_TYPE = [
  { value: 0, label: "พิพากษาปกติ" },
  { value: 1, label: "พิพากษาตารางดิว" },
];

const OPTIONS_ASSET_STATUS = [
  { value: "all", label: "ทั้งหมด" },
  { value: "null", label: "ยังไม่ได้ส่งสืบ" },
  { value: "1", label: "รอส่งสืบ" },
  { value: "2", label: "ดำเนินการสืบ" },
  { value: "3", label: "เจอทรัพย์" },
  { value: "4", label: "ไม่เจอทรัพย์" },
];

const OPTIONS_ASSET_TYPE = [
  { value: "all", label: "ทั้งหมด" },
  { value: "1", label: "รถ" },
  { value: "2", label: "ที่ดิน" },
];

const ASSET_TYPE_LABEL_BY_VALUE = {
  1: "รถ",
  2: "ที่ดิน",
};

const ASSET_STATUS_META = {
  null: { color: "default", label: "ยังไม่ได้ส่งสืบ" },
  1: { color: "orange", label: "รอส่งสืบ" },
  2: { color: "blue", label: "ดำเนินการสืบ" },
  3: { color: "green", label: "เจอทรัพย์" },
  4: { color: "red", label: "ไม่เจอทรัพย์" },
};

const getAssetTypeLabel = (SCHEMA, STRNO, product) => {
  if (SCHEMA === "lsfhp") return "ที่ดิน";
  if (SCHEMA === "vsfhp") return "รถ";
  if (SCHEMA === "rpsl" || SCHEMA === "psfhp" || SCHEMA === "sfhp") {
    const raw = String(STRNO ?? "").trim();
    if (!raw) return "ไม่ระบุ";

    const hasAlpha = /[a-zA-Z]/.test(raw);
    const hasDigit = /[0-9]/.test(raw);
    if (hasAlpha && hasDigit) return "รถ";

    if (raw.includes("-")) return "รถ";
    if (raw.includes(",")) return "ที่ดิน";

    const digitsOnly = raw.replace(/[()]/g, "");
    if (/^[0-9]+$/.test(digitsOnly)) {
      return digitsOnly.length <= 6 ? "ที่ดิน" : "รถ";
    }

    return "ไม่ระบุ";
  }

  return "ไม่ระบุ";
};

const getCaseTypeLabel = (flagLoanNew, flagDue) =>
  flagLoanNew === 1
    ? "ทำยอม"
    : flagLoanNew === 2 && flagDue === 0
      ? "พิพากษาปกติ"
      : flagLoanNew === 2 && flagDue === 1
        ? "พิพากษาปกติมีตารางดิว"
        : "-";

const formatThaiDate = (d) =>
  d ? dayjs(d).add(543, "year").format("DD/MM/YYYY") : "-";

const getDefendantName = (d) =>
  `${d?.SNAM || ""}${d?.NAME1 || ""} ${d?.NAME2 || ""}`.trim() || "-";

// จัดเรียงจำเลย: รายการที่ไม่มี defendantNumber (เช่น คู่สมรส) ให้แทรกต่อท้าย
// จำเลยหลักที่ relationCustomerId ตรงกับ customerId ทันที
const sortDefendantsWithRelations = (defendants) => {
  const mains = defendants.filter((d) => d.defendantNumber != null);
  const relations = defendants.filter((d) => d.defendantNumber == null);
  const used = new Set();
  const result = [];

  mains.forEach((main) => {
    result.push(main);
    relations.forEach((rel) => {
      if (rel.relationCustomerId === main.customerId) {
        result.push(rel);
        used.add(rel);
      }
    });
  });

  relations.forEach((rel) => {
    if (!used.has(rel)) result.push(rel);
  });

  return result;
};

// สถานะผลสืบทรัพย์: อิงจาก statusActiveInvestigate
// null = ยังไม่ได้ส่งสืบ, 1 = รอส่งสืบ, 2 = ดำเนินการสืบ, 3 = เจอทรัพย์, 4 = ไม่เจอทรัพย์
const getAssetStatus = (record) => {
  const status = record.statusActiveInvestigate;
  return status === null || status === undefined ? "null" : String(status);
};

const getAssetStatusMeta = (status) =>
  ASSET_STATUS_META[status] || ASSET_STATUS_META.null;

// ที่อยู่ของจำเลยแต่ละคน อ้างอิงจากที่ดินที่พบ (assetLand) เนื่องจากไม่มีฟิลด์ที่อยู่แยกต่างหาก
const formatLandAddress = (land) =>
  [
    land?.numberLand && `เลขที่ ${land.numberLand}`,
    (land?.subDistDesc ?? land?.subdistrict) &&
      `ต.${land.subDistDesc ?? land.subdistrict}`,
    (land?.distDesc ?? land?.district) && `อ.${land.distDesc ?? land.district}`,
    (land?.provDesc ?? land?.province) && `จ.${land.provDesc ?? land.province}`,
  ]
    .filter(Boolean)
    .join(" ");

// สรุปผลของจำเลยแต่ละคน: ที่อยู่/พบหรือไม่พบ/หมายเหตุ
// ก่อนเคสจะถูกสืบเสร็จ (caseStatus ยังไม่ใช่ 3/4) ยังสรุปผลรายคนไม่ได้
const getDefendantAssetInfo = (defendant, caseStatus) => {
  const lands = defendant.assetLand || [];

  if (caseStatus !== "3" && caseStatus !== "4") {
    const meta = getAssetStatusMeta(caseStatus);
    return { found: null, address: "-", remark: meta.label };
  }

  const found = lands.length > 0;
  return {
    found,
    address: found ? lands.map(formatLandAddress).join(" / ") : "-",
    remark: found ? `พบ ${lands.length} แปลง` : "-",
  };
};

const Main = () => {
  const [, , currencyFormatPoint] = CurrencyFormat();

  const userId = parseInt(localStorage.getItem("USER_ID"));
  const [signOut] = TokenCheck();

  const [loading, setLoading] = useState(false);
  const [arrayPay, setArrayPay] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [caseTypeFilter, setCaseTypeFilter] = useState(null);
  const [caseType, setCaseType] = useState(3);
  const [assetStatusFilter, setAssetStatusFilter] = useState(["all"]);
  const [searchContno, setSearchContno] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // "ทั้งหมด" เป็นตัวเลือกแบบ exclusive: ถ้าอยู่ในค่าที่เลือกให้เหลือแค่ "ทั้งหมด"
  // ผู้ใช้ต้องเอา "ทั้งหมด" ออกก่อนถึงจะเลือกตัวเลือกอื่นเพิ่มได้
  const handleAssetStatusFilterChange = useCallback((values) => {
    setAssetStatusFilter(values.includes("all") ? ["all"] : values);
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [assetType, setAssetType] = useState("all");

  // ── Query ─────────────────────────────────────────────
  const queryData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        POST_LOAD_JUDGE_ASSET,
        { userId: 1, type: caseType },
        { headers: HEADERS_EXPORT_BEN },
      );
      const data = Array.isArray(res.data.mixData) ? res.data.mixData : [];

      setArrayPay(data);
      setFilteredData(data);
      setCurrentPage(1);
      setSelectedRowKeys([]);
    } catch (err) {
      message.error(
        err?.response?.status === 404 ? "ไม่พบข้อมูล" : "เกิดข้อผิดพลาด",
      );
      if (err?.response?.status === 401) {
        signOut();
      }
      setArrayPay([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [signOut, caseType]);

  // ── Filter (มีผลเฉพาะตอนกดปุ่ม "กรองข้อมูล") ──────────
  const handleFilter = useCallback(() => {
    let filtered = arrayPay;

    if (caseTypeFilter !== null) {
      filtered = filtered.filter((d) => d.flagDue === caseTypeFilter);
    }

    if (!assetStatusFilter.includes("all")) {
      filtered = filtered.filter((d) =>
        assetStatusFilter.includes(getAssetStatus(d)),
      );
    }

    if (assetType !== "all") {
      const typeLabel = ASSET_TYPE_LABEL_BY_VALUE[assetType];
      filtered = filtered.filter(
        (d) => getAssetTypeLabel(d.SCHEMA, d.STRNO, d.product) === typeLabel,
      );
    }

    const query = searchContno.trim().toLowerCase();
    if (query) {
      filtered = filtered.filter(
        (d) =>
          d.CONTNO?.toLowerCase().includes(query) ||
          d.ISSUNO?.toLowerCase().includes(query),
      );
    }

    setFilteredData(filtered);
    setSelectedRowKeys([]);
    setCurrentPage(1);
  }, [arrayPay, caseTypeFilter, assetStatusFilter, assetType, searchContno]);

  const hasActiveFilters =
    caseTypeFilter !== 3 ||
    !assetStatusFilter.includes("all") ||
    assetType !== "all" ||
    !!searchContno.trim();

  const handleResetFilters = useCallback(() => {
    setCaseTypeFilter(null);
    setAssetType("all");
    setAssetStatusFilter(["all"]);
    setSearchContno("");
    setFilteredData(arrayPay);
    setSelectedRowKeys([]);
    setCurrentPage(1);
  }, [arrayPay]);

  // ── แปลงข้อมูลคดี → แถวข้อมูลเรียงลงมาแบบรายคน (1 แถว/จำเลย 1 คน) ──
  // ลำดับ/รายละเอียดคดี จะถูก merge (rowSpan) ครอบคลุมทุกแถวของจำเลยในคดีนั้น
  const buildFlatRows = (caseList, indexOffset) => {
    const rows = [];
    caseList.forEach((caseItem, localIdx) => {
      const caseIndex = indexOffset + localIdx + 1;
      const defendants = sortDefendantsWithRelations(
        caseItem.judgeDefAsset || [],
      );
      const caseStatus = getAssetStatus(caseItem);

      if (defendants.length === 0) {
        rows.push({
          key: `case-${caseItem.idJudge}`,
          caseItem,
          caseIndex,
          isFirstOfCase: true,
          isLastOfCase: true,
          caseRowSpan: 1,
          defendant: null,
        });
        return;
      }

      defendants.forEach((defendant, di) => {
        rows.push({
          key: `def-${caseItem.idJudge}-${defendant.idJudgeDef ?? di}`,
          caseItem,
          caseIndex,
          isFirstOfCase: di === 0,
          isLastOfCase: di === defendants.length - 1,
          caseRowSpan: di === 0 ? defendants.length : 0,
          defendant,
          caseStatus,
          assetInfo: getDefendantAssetInfo(defendant, caseStatus),
        });
      });
    });
    return rows;
  };

  const pagedCaseData = useMemo(
    () =>
      filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filteredData, currentPage, pageSize],
  );

  const flatRows = useMemo(
    () => buildFlatRows(pagedCaseData, (currentPage - 1) * pageSize),
    [pagedCaseData, currentPage, pageSize],
  );

  // ── Export helpers (เลือกเฉพาะที่ติ๊ก หรือทั้งหมดที่กรองไว้ถ้าไม่ได้เลือก) ──
  const selectedCaseIds = useMemo(() => {
    const idSet = new Set();
    flatRows.forEach((r) => {
      if (selectedRowKeys.includes(r.key)) idSet.add(r.caseItem.idJudge);
    });
    return idSet;
  }, [flatRows, selectedRowKeys]);

  const selectedRows = useMemo(
    () => filteredData.filter((r) => selectedCaseIds.has(r.idJudge)),
    [filteredData, selectedCaseIds],
  );

  const exportRows = selectedRows.length ? selectedRows : filteredData;

  // ── Columns หลัก (แสดงข้อมูลเรียงลงมาแบบรายคน) ────────────
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 60,
      render: (_, row) => ({
        children: row.caseIndex,
        props: {
          rowSpan: row.isFirstOfCase ? row.caseRowSpan : 0,
          style: { verticalAlign: "middle" },
        },
      }),
    },
    {
      title: "คดี",
      key: "detail",
      width: 160,
      render: (_, row) => {
        const r = row.caseItem;
        return {
          children: (
            <div style={{ fontSize: 13, lineHeight: 1.7 }}>
              <div>
                <Text strong>เลขคดีแดง {r.redCaseNumber || "-"}</Text>
              </div>
              <div style={{ color: "#888" }}>
                เลขสัญญา{" "}
                <Text style={{ color: "#888" }} copyable>
                  {r.CONTNO || "-"}
                </Text>
              </div>
              <Space size={4} wrap style={{ marginTop: 2 }}>
                <Tag
                  color={
                    r.flagLoanNew === 1
                      ? "green"
                      : r.flagLoanNew === 2 && r.flagDue === 1
                        ? "purple"
                        : "blue"
                  }
                >
                  {getCaseTypeLabel(r.flagLoanNew, r.flagDue)}
                </Tag>
                <Tag
                  color={
                    r.flagLoanNew === 1
                      ? "green"
                      : r.flagLoanNew === 2 && r.flagDue === 1
                        ? "purple"
                        : "blue"
                  }
                >
                  {getAssetTypeLabel(r.SCHEMA, r.STRNO, r.product)}
                </Tag>
              </Space>
            </div>
          ),
          props: {
            rowSpan: row.isFirstOfCase ? row.caseRowSpan : 0,
            style: { verticalAlign: "middle" },
          },
        };
      },
    },
    {
      title: "ยอดหนี้",
      key: "money",
      width: 140,
      render: (_, row) => {
        const r = row.caseItem;
        return {
          children: (
            <div style={{ fontSize: 13, lineHeight: 1.7 }}>
              <div>ตามคำพิพากษา: {currencyFormatPoint(r.totalJudge)} บาท</div>
              <div>
                คงเหลือ:{" "}
                <Text strong style={{ color: "#cf1322" }}>
                  {currencyFormatPoint(r.resJudge)}
                </Text>{" "}
                บาท
              </div>
              <div>
                จ่ายล่าสุด:{" "}
                <Text
                  strong
                  style={{ color: r.lastPayDate ? "green" : "gray" }}
                >
                  {r.lastPayDate ? formatThaiDate(r.lastPayDate) : "-"}
                </Text>{" "}
              </div>
            </div>
          ),
          props: {
            rowSpan: row.isFirstOfCase ? row.caseRowSpan : 0,
            style: { verticalAlign: "middle" },
          },
        };
      },
    },
    {
      title: "ผลสืบทรัพย์ (คดี)",
      key: "caseStatus",
      width: 140,
      render: (_, row) => {
        const r = row.caseItem;
        const status = getAssetStatus(r);
        const meta = getAssetStatusMeta(status);
        return {
          children: (
            <div style={{ fontSize: 12, lineHeight: 1.7 }}>
              <Tag color={meta.color}>{meta.label}</Tag>
              {r.dateOnlineInvestigate && (
                <div style={{ color: "#888" }}>
                  สืบออนไลน์: {formatThaiDate(r.dateOnlineInvestigate)}
                </div>
              )}
              {r.responseDateInvestigate && (
                <div style={{ color: "#888" }}>
                  วันที่ลงข้อมูล: {formatThaiDate(r.responseDateInvestigate)}
                </div>
              )}
            </div>
          ),
          props: {
            rowSpan: row.isFirstOfCase ? row.caseRowSpan : 0,
            style: { verticalAlign: "middle" },
          },
        };
      },
    },
    {
      title: "รายชื่อจำเลย",
      key: "defendantName",
      width: 70,
      render: (_, row) =>
        row.defendant ? (
          <Text strong style={{ fontSize: 13 }}>
            {row.defendant.defendantNumber
              ? `${row.defendant.defendantNumber}. `
              : ""}
            {getDefendantName(row.defendant)}
            {row.defendant.relation && (
              <Text type="secondary" style={{ fontWeight: 400 }}>
                {" "}
                ({row.defendant.relation})
              </Text>
            )}
          </Text>
        ) : (
          <Text type="secondary">ไม่มีรายชื่อจำเลย</Text>
        ),
    },
    {
      title: "เลขบัตรประชาชน",
      key: "cuscod",
      align: "center",
      width: 150,
      render: (_, row) => row.defendant?.CUSCOD || "-",
    },
    {
      title: "ที่อยู่ / ทรัพย์ที่พบ",
      key: "address",
      width: 400,
      render: (_, row) => (
        <span
          style={{
            fontSize: 13,
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {row.assetInfo?.address || "-"}
        </span>
      ),
    },
    {
      title: "ผลตรวจสอบ",
      key: "foundStatus",
      align: "center",
      width: 110,
      render: (_, row) => {
        if (!row.defendant) return "-";
        const { found } = row.assetInfo;
        if (found === null) {
          const meta = getAssetStatusMeta(row.caseStatus);
          return <Tag color={meta.color}>{meta.label}</Tag>;
        }
        return found ? (
          <Tag color="green">พบ</Tag>
        ) : (
          <Tag color="red">ไม่พบ</Tag>
        );
      },
    },
    {
      title: "หมายเหตุ",
      key: "remark",
      width: 160,
      render: (_, row) => (
        <span style={{ fontSize: 13, color: "#888" }}>
          {row.assetInfo?.remark || "-"}
        </span>
      ),
    },
  ];

  // ── Export Excel: ข้อมูลเรียงลงมาแบบรายคน (merge ลำดับ/เลขสัญญาต่อคดี) ──
  const buildExportFlatRows = () => {
    const rows = [];
    const groups = [];

    exportRows.forEach((caseItem, idx) => {
      const defendants = sortDefendantsWithRelations(
        caseItem.judgeDefAsset || [],
      );
      const caseStatus = getAssetStatus(caseItem);
      const startRow = rows.length;

      const assetTypeLabel = getAssetTypeLabel(
        caseItem.SCHEMA,
        caseItem.STRNO,
        caseItem.product,
      );

      if (defendants.length === 0) {
        rows.push({
          ลำดับ: idx + 1,
          เลขคดีแดง: caseItem.redCaseNumber || "-",
          เลขสัญญา: caseItem.CONTNO || "-",
          ประเภททรัพย์: assetTypeLabel,
          รายชื่อ: "-",
          รหัสบัตร: "-",
          วันที่จ่ายล่าสุด: caseItem.lastPayDate
            ? formatThaiDate(caseItem.lastPayDate)
            : "-",
          ที่อยู่: "-",
          ไม่พบ: "",
          พบ: "",
          หมายเหตุ: "ไม่มีรายชื่อจำเลย",
        });
      } else {
        defendants.forEach((d) => {
          const info = getDefendantAssetInfo(d, caseStatus);
          rows.push({
            ลำดับ: "",
            เลขคดีแดง: "",
            เลขสัญญา: "",
            ประเภททรัพย์: "",
            รายชื่อ: `${d.defendantNumber ? `${d.defendantNumber}. ` : ""}${getDefendantName(d)}${d.relation ? ` (${d.relation})` : ""}`,
            รหัสบัตร: d.CUSCOD || "-",
            วันที่จ่ายล่าสุด: caseItem.lastPayDate
              ? formatThaiDate(caseItem.lastPayDate)
              : "-",
            ที่อยู่: info.address,
            ไม่พบ: info.found === false ? "✓" : "",
            พบ: info.found === true ? "✓" : "",
            หมายเหตุ: info.remark,
          });
        });
        rows[startRow].ลำดับ = idx + 1;
        rows[startRow].เลขคดีแดง = caseItem.redCaseNumber || "-";
        rows[startRow].เลขสัญญา = caseItem.CONTNO || "-";
        rows[startRow].ประเภททรัพย์ = assetTypeLabel;
      }

      groups.push({ startRow, endRow: rows.length - 1 });
    });

    return { rows, groups };
  };

  const handleExportExcel = () => {
    const { rows, groups } = buildExportFlatRows();
    if (!rows.length) {
      message.warning("ไม่มีข้อมูลสำหรับ Export");
      return;
    }

    const keys = Object.keys(rows[0]);
    const ws = XLSX.utils.json_to_sheet(rows);

    const headerFill = { patternType: "solid", fgColor: { rgb: "344962" } };
    const evenFill = { patternType: "solid", fgColor: { rgb: "EBF5FF" } };
    const oddFill = { patternType: "solid", fgColor: { rgb: "FFFFFF" } };
    const foundFill = { patternType: "solid", fgColor: { rgb: "E6F7E6" } };
    const notFoundFill = { patternType: "solid", fgColor: { rgb: "FDECEA" } };
    const groupTopBorder = { style: "medium", color: { rgb: "344962" } };
    const thinBorder = { style: "thin", color: { rgb: "CCCCCC" } };

    const groupStartRows = new Set(groups.flatMap((g) => [g.startRow]));

    // header
    keys.forEach((_, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!ws[cellRef]) return;
      ws[cellRef].s = {
        fill: headerFill,
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: thinBorder,
          bottom: thinBorder,
          left: thinBorder,
          right: thinBorder,
        },
      };
    });

    // data rows
    rows.forEach((row, ri) => {
      const isGroupStart = groupStartRows.has(ri);
      keys.forEach((key, ci) => {
        const cellRef = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (!ws[cellRef]) ws[cellRef] = { v: "", t: "s" };

        let fill = ri % 2 === 0 ? evenFill : oddFill;
        if (key === "ไม่พบ" && row.ไม่พบ) fill = notFoundFill;
        if (key === "พบ" && row.พบ) fill = foundFill;

        ws[cellRef].s = {
          fill,
          font: {
            sz: 11,
            bold: key === "ลำดับ" || key === "เลขคดีแดง" || key === "เลขสัญญา",
          },
          alignment: {
            horizontal: [
              "ลำดับ",
              "เลขคดีแดง",
              "ประเภททรัพย์",
              "ไม่พบ",
              "พบ",
              "รหัสบัตร",
              "วันที่จ่ายล่าสุด",
            ].includes(key)
              ? "center"
              : "left",
            vertical: "center",
            wrapText: true,
          },
          border: {
            top: isGroupStart ? groupTopBorder : thinBorder,
            bottom: thinBorder,
            left: thinBorder,
            right: thinBorder,
          },
        };
      });
    });

    // merge เซลล์ ลำดับ/เลขสัญญา ครอบคลุมทุกแถวของจำเลยในคดีเดียวกัน
    ws["!merges"] = groups
      .filter((g) => g.endRow > g.startRow)
      .flatMap((g) => [
        { s: { r: g.startRow + 1, c: 0 }, e: { r: g.endRow + 1, c: 0 } },
        { s: { r: g.startRow + 1, c: 1 }, e: { r: g.endRow + 1, c: 1 } },
        { s: { r: g.startRow + 1, c: 2 }, e: { r: g.endRow + 1, c: 2 } },
        { s: { r: g.startRow + 1, c: 3 }, e: { r: g.endRow + 1, c: 3 } },
      ]);

    ws["!cols"] = [
      { wch: 8 },
      { wch: 16 },
      { wch: 16 },
      { wch: 12 },
      { wch: 28 },
      { wch: 20 },
      { wch: 16 },
      { wch: 45 },
      { wch: 10 },
      { wch: 10 },
      { wch: 35 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "รายงาน");
    XLSX.writeFile(
      wb,
      `รายงานคดีพิพากษาและทรัพย์สินจำเลย_${dayjs().format("DDMMYYYY")}.xlsx`,
    );
    message.success("Export Excel สำเร็จ");
  };

  return (
    <>
      <style>{`
      .row-even td { background-color: #f5f8ff !important; }
      .row-odd td  { background-color: #ffffff !important; }
      .case-group-end td { border-bottom: 2px solid #d9e2ef !important; }
      .judge-assets-table .ant-table-thead > tr > th {
        background: #344962 !important;
        color: #fff !important;
        font-weight: 600;
      }
    `}</style>
      <Spin spinning={loading} size="large" tip="Loading...">
        <Card style={{ marginBottom: 10, borderRadius: 12 }}>
          <Divider style={{ fontSize: 20 }}>
            รายงานคดีพิพากษาและทรัพย์สินจำเลย
          </Divider>
          <Card
            size="small"
            title={
              <Space>
                <FilterOutlined />
                <span>โหลดข้อมูล</span>
              </Space>
            }
            style={{
              marginBottom: 12,
              background: "#fafafa",
              borderRadius: 10,
            }}
          >
            <Row gutter={[16, 12]} align="bottom">
              <Col xs={24} sm={12} md={8} lg={4}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ประเภทการค้นหา
                </Text>
                <div>
                  <Radio.Group
                    options={OPTIONS_CASE_TYPE}
                    value={caseType}
                    onChange={(e) => setCaseType(e.target.value)}
                  />
                </div>
              </Col>

              <Col flex="auto" style={{ textAlign: "right" }}>
                <Space>
                  <Button
                    color="orange"
                    variant="outlined"
                    icon={<FilterOutlined />}
                    onClick={queryData}
                    style={{ borderRadius: 8 }}
                  >
                    โหลดข้อมูล
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
          {/* ── แถบกรองข้อมูล ── */}
          <Card
            size="small"
            title={
              <Space>
                <FilterOutlined />
                <span>ตัวกรองข้อมูล</span>
              </Space>
            }
            style={{
              marginBottom: 12,
              background: "#fafafa",
              borderRadius: 10,
            }}
          >
            <Row gutter={[16, 12]} align="bottom">
              <Col xs={24} sm={12} md={8} lg={6}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ค้นหาเลขที่สัญญา
                </Text>
                <Input
                  placeholder="เช่น V2-0009803"
                  value={searchContno}
                  onChange={(e) => setSearchContno(e.target.value)}
                  onPressEnter={handleFilter}
                  prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                  allowClear
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Col>

              <Col xs={24} sm={12} md={8} lg={5}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ผลสืบทรัพย์
                </Text>
                <Select
                  mode="multiple"
                  style={{ width: "100%" }}
                  options={OPTIONS_ASSET_STATUS}
                  value={assetStatusFilter}
                  onChange={handleAssetStatusFilterChange}
                  maxTagCount="responsive"
                  size="large"
                />
              </Col>
              <Col xs={24} sm={12} md={8} lg={5}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ประเภททรัพย์
                </Text>
                <Select
                  style={{ width: "100%" }}
                  options={OPTIONS_ASSET_TYPE}
                  value={assetType}
                  onChange={setAssetType}
                  size="large"
                />
              </Col>

              <Col xs={24} sm={12} md={8} lg={4}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ประเภทการกรอง
                </Text>
                <div>
                  <Radio.Group
                    options={OPTIONS_FILTER_CASE_TYPE}
                    value={caseTypeFilter}
                    onChange={(e) => setCaseTypeFilter(e.target.value)}
                    disabled={caseType !== 3}
                  />
                </div>
              </Col>

              <Col flex="auto" style={{ textAlign: "right" }}>
                <Space>
                  <Button
                    color="orange"
                    variant="outlined"
                    icon={<FilterOutlined />}
                    onClick={handleFilter}
                    style={{ borderRadius: 8 }}
                  >
                    กรองข้อมูล
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleResetFilters}
                    color="red"
                    variant="outlined"
                    style={{ borderRadius: 8 }}
                  >
                    ล้างตัวกรอง
                  </Button>
                  <Button
                    icon={<FileExcelOutlined />}
                    onClick={handleExportExcel}
                    disabled={!filteredData.length}
                    style={{
                      borderRadius: 8,
                      fontWeight: 600,
                      color: "#16a34a",
                      borderColor: "#16a34a",
                    }}
                  >
                    Export Excel
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* ── สรุปตัวกรองที่ใช้อยู่ ── */}
          {hasActiveFilters && (
            <Card
              size="small"
              style={{
                background: "#f0f5ff",
                marginBottom: 12,
                borderRadius: 10,
              }}
            >
              <Space split="|" size="large" wrap>
                <span>
                  <strong>ผลการค้นหา:</strong> {filteredData.length} รายการ
                </span>
                {searchContno && (
                  <span>
                    <strong>ค้นหา:</strong> "{searchContno}"
                  </span>
                )}
                {!assetStatusFilter.includes("all") && (
                  <span>
                    <strong>ผลสืบทรัพย์:</strong>{" "}
                    {OPTIONS_ASSET_STATUS.filter((o) =>
                      assetStatusFilter.includes(o.value),
                    )
                      .map((o) => o.label)
                      .join(", ")}
                  </span>
                )}
              </Space>
            </Card>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text
              strong
              style={{
                margin: "10px 0",
              }}
            >
              จำนวนสัญญาทั้งหมด : {arrayPay.length} เคส
              {selectedRowKeys.length > 0
                ? ` (เลือก ${selectedRowKeys.length} รายการ)`
                : " (ไม่เลือกรายการ = Export ข้อมูลทั้งหมด)"}
            </Text>
          </div>

          <Table
            className="judge-assets-table"
            style={{ marginTop: 10, borderRadius: 10, overflow: "hidden" }}
            size="small"
            bordered
            columns={columns}
            dataSource={flatRows}
            rowKey={(r) => r.key}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
              getCheckboxProps: (row) => ({
                disabled: !row.isFirstOfCase,
                style: row.isFirstOfCase ? undefined : { display: "none" },
              }),
            }}
            rowClassName={(row) =>
              [
                row.caseIndex % 2 === 0 ? "row-even" : "row-odd",
                row.isLastOfCase ? "case-group-end" : "",
              ]
                .filter(Boolean)
                .join(" ")
            }
            locale={{
              emptyText: <Empty description="ไม่มีข้อมูล" />,
            }}
            scroll={{ x: 1300 }}
            pagination={false}
          />
          <div style={{ marginTop: 12, textAlign: "right" }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={filteredData.length}
              showSizeChanger
              showTotal={(t) => `ทั้งหมด ${t} รายการ`}
              pageSizeOptions={["10", "20", "50", "100"]}
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
            />
          </div>
        </Card>
      </Spin>
    </>
  );
};

const MainJudgeAssets = MotionHoc(Main);
export default MainJudgeAssets;
